import { receiveVoiceHint } from '@/features/voice/voice-hints';
import { reconcileVisibleVoice } from '@/features/voice/queries';
import { ownedTrackingHint, trackingHintTypes, type TrackingHint } from '@/features/team-chat/tracking-event';
import { chatInstant } from '@/features/team-chat/message-order';
import { messageHintTypes, ownedMessageHint } from '@/features/team-chat/message-event';
import { messageReadSelector, readOwnedMessages } from '@/features/team-chat/message-reads';
import { reconcileChatMessages, isCurrentMessageOwner, messageKey, ownsMessageKey } from '@/features/team-chat/message-cache';
import { ownedWAEventHint, reconcileWAEvents } from '@/features/inbox/wa-event-keys';
import { reconcileDashboardReads, reconcileAllDashboardReads } from '@/features/dashboard/realtime-keys';
import { currentCustomerPanelKey } from '@/features/inbox/customer-panel-keys';
import {currentMessageHistoryKey} from '@/features/inbox/message-menu-keys';
import {contextKey,currentContextKey,retainNewerContext,type ConversationContext} from '@/features/inbox/conversation-context-keys';
import { receiveAshleySuggestionHint } from './ashley-suggestion-readback';
import {parseContextUpdated,parseAIProposal} from '@/features/inbox/conversation-context-events';
import { contactExportKey } from '@/features/contacts/contact-export-keys';
import { openConversationChannelsKey } from '@/features/inbox/open-conversation-keys';
import { contactImportChannelsKey } from '@/features/contacts/contact-import-keys';
import { contactPurgeKey, isContactPurgeId } from '@/features/contacts/contact-purge-keys';
import { crmListKey, isCurrentCRMContactKey, isCurrentContactPipelineKey, isCurrentKanbanKey } from '@/features/contacts/contact-query-keys';
import { reconcileCalls } from '@/features/calls/call-reads';
import { receiveCallHint } from '@/features/calls/call-hints';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '@/store/auth-store';
import { sessionScope, sessionTenantId, sessionRevision } from '@/features/auth/session-scope';
import { reconcilePlatformTransferQueries } from '@/features/inbox/platform-transfer-queries';
import { ownedAppCatalogHint, reconcileHostApps } from '@/features/apps/host-keys';
import { ownedPluginHint, reconcilePluginHint } from '@/features/plugins/inbox/keys';
import { normalizeLocale } from '@/lib/i18n/locales';
import { usePermissionsStore, isConversationVisible } from '@/store/permissions-store';
import { queryClient } from '@/lib/query/query-client';
import { usePresenceStore, normalizeStatus } from '@/lib/presence';
import { useTypingStore } from '@/lib/realtime/typing-store';
import {
  shouldJoinOnMemberAdded,
  isSelfMemberRemoved,
  channelTypingKey,
  parseChannelTypingEvent,
  buildMentionNotification,
  buildThreadNotification,
} from '@/lib/realtime/channel-realtime';
import {
  effectiveNotifyPref,
  getChannelNotifyPref,
  shouldNotifyChannelMessage,
  type ChannelNotifyPref,
} from '@/features/team-chat/channel-notify-prefs';
import { type Channel } from '@/features/team-chat/queries';
import { captureChatOwner, currentChatChannel, currentChatChannelsKey } from '@/features/team-chat/creation-actions';
import { channelHintTypes, ownedChannelHint } from '@/features/team-chat/channel-event';
import { dmDisplayName } from '@/features/team-chat/dm-display';
import { useChatDockStore } from '@/features/team-chat/chat-dock-store';
import { isDocumentAttended, isTeamChatAttended } from '@/features/team-chat/attention-view';
import { removeLeftChannel } from '@/features/team-chat/leave-channel-cache';
import { useTeamChatLiveAlertStore } from '@/features/team-chat/live-alert-store';

import { isStatusAdvance, isKnownStatus } from '@/features/inbox/message-status';
import { readTemplatePresentation } from '@/features/inbox/template-presentation';
import { readDeliveryFailurePresentation } from '@/features/inbox/delivery-failure-presentation';
import { useInboxPresenceStore } from '@/features/inbox/inbox-presence-store';
import { useBtsStore, readBtsResultHint } from '@/features/inbox/bts';
import { useAiSuggestionStore } from '@/lib/realtime/ai-suggestion-store';
import { useInboxAgentSuggestionStore } from '@/features/inbox/inbox-agent-suggestion-store';
import { useVoicePresenceStore } from '@/features/voice/voice-presence-store';
import { useVoiceSessionStore } from '@/features/voice/voice-session-store';
import { shouldTearDownVoice } from '@/features/voice/voice-teardown';
import { notificationSound } from '@/lib/notifications/sound-service';
import { receiveNotificationHint, reconcileNotifications } from '@/features/notifications/realtime';
import { alertPreview,commsText,messageAlertCopy,previewProductCopy } from '@/features/team-chat/alert-copy';
import { showNotification } from '@/lib/notifications/browser-service';
import { customFieldLibraryKey } from '@/features/custom-fields/field-settings';
import { tagLibraryKey } from '@/features/settings/tag-settings';
import { macroLibraryKey } from '@/features/settings/macro-settings';
import { quickReplyLibraryKey } from '@/features/settings/reply-settings';
import { notificationSettingsKey, type NotificationSettings } from '@/features/settings/notification-settings';
import { apiOrigin, rawWsUrl } from '@/api/api-config';
import { isModuleEnabled, type ModulesMap } from '@/features/modules/queries';
import {
  conversationCacheContains,
  transformConversationCache,
} from '@/features/inbox/conversation-cache';
import type { ChannelResponse, TagResponse } from '@blazeteams/sdk';
import { patchConversationContactCache } from '@/features/inbox/contact-cache';
import {
  extractInboxMessagePayload,
  normalizeInboxMessage,
} from '@/features/inbox/message-media';

type ConvForCache = import('@/features/inbox/conversation-event-preview').ConversationListRow;

/**
 * Apply a list transform to EVERY cached conversation list, whatever its shape.
 * The non-paginated query (`inboxQueries.conversations`) caches a flat `Conv[]`;
 * the paginated inbox list (`inboxQueries.conversationsInfinite`) caches
 * `{ pages: { items: Conv[], … }[], pageParams }`. Both live under the
 * `['conversations']` prefix, so one `setQueriesData` reaches both — we branch on
 * the shape so the infinite query's per-page `items` get patched too, preserving
 * the documented INSTANT surgical removal on the primary inbox list instead of
 * waiting for the follow-up refetch. Pagination keys off each page's `rawCount`
 * (not `items.length`), so shrinking `items` here is safe.
 */
function patchConversationCaches(transform: (list: ConvForCache[]) => ConvForCache[]): void {
  queryClient.setQueriesData<unknown>({ queryKey: ['conversations'] }, (prev: unknown) => {
    return transformConversationCache(prev, transform);
  });
}

/** Kanban events don't consistently include contact_id; refresh every loaded contact badge. */
function invalidateContactPipelineStatuses(): void {
  void queryClient.invalidateQueries({
    predicate: (query) =>
      currentContactMetadata(query.queryKey) && query.queryKey[2] === 'pipeline-status',
  });
}

function currentContactMetadata(key: readonly unknown[], id?: string) {
  const scope = sessionScope(useAuthStore.getState());
  if (key[0] !== 'contact' || id && key[1] !== id) return false;
  const scoped = typeof key[3] === 'string' && (key[3] === 'anonymous' || key[3].startsWith('['));
  return !scoped || key[3] === scope;
}
function currentContactList(key: readonly unknown[]) {
  return key[0] === 'contacts' && (key[1] !== 'crm' || key[2] === sessionScope(useAuthStore.getState()));
}
function reconcileContactLists() { return queryClient.invalidateQueries({ predicate: query => currentContactList(query.queryKey) }); }
function reconcileContactMetadata(id?: string) { return queryClient.invalidateQueries({ predicate: query => currentContactMetadata(query.queryKey, id) }); }

/** Restart reads that began before the hint, including an initial fetch. */
async function reconcilePanelQueries(match: (key: readonly unknown[]) => boolean) {
  const revision = sessionRevision();
  const filter = { predicate: (query: { queryKey: readonly unknown[] }) => currentCustomerPanelKey(query.queryKey) && match(query.queryKey) };
  await queryClient.cancelQueries(filter);
  if (revision === sessionRevision()) await queryClient.invalidateQueries(filter);
}

/** Use hints only to reread current-session CRM activity, never to fabricate counters. */
function reconcileCRMActivity(conversationId: string | undefined, data: Record<string, unknown> | undefined, tenantId?: unknown) {
  const owner = sessionTenantId(useAuthStore.getState());
  if (!owner || [tenantId, data?.tenant_id].some(value => value !== undefined && value !== owner)) return;
  const contactIds = new Set<string>();
  if (typeof data?.contact_id === 'string' && data.contact_id) contactIds.add(data.contact_id);
  for (const query of queryClient.getQueryCache().findAll({ predicate: query => (isCurrentCRMContactKey(query.queryKey) && query.queryKey[2] === 'conversations') || (currentCustomerPanelKey(query.queryKey) && query.queryKey[2] === 'panel-conversations') })) {
    const cached = query.state.data;
    const rows = query.queryKey[2] === 'panel-conversations' && cached && typeof cached === 'object' && 'pages' in cached && Array.isArray(cached.pages) ? cached.pages.flatMap(page => page.items ?? []) : cached;
    if (conversationId && Array.isArray(rows) && rows.some(row => row?.id === conversationId) && typeof query.queryKey[1] === 'string') contactIds.add(query.queryKey[1]);
  }
  void reconcilePanelQueries(key => key[0] === 'contact' && key[2] === 'panel-conversations' && (!contactIds.size || contactIds.has(String(key[1]))));
  if (!contactIds.size) return;
  // Reuse an in-flight read during a burst instead of repeatedly cancelling it.
  void queryClient.invalidateQueries({ predicate: query => isCurrentCRMContactKey(query.queryKey) && contactIds.has(String(query.queryKey[1])) && ['detail', 'stats', 'conversations'].includes(String(query.queryKey[2])) }, { cancelRefetch: false });
}

/** Reconcile authoritative metadata; a tenant event never changes personal UI language. */
function invalidateTenantLanguage(): void {
  const state = useAuthStore.getState();
  if (!state.user?.id || !sessionTenantId(state) || state.impersonation) return;
  const scope = sessionScope(state);
  void queryClient.invalidateQueries({ queryKey: ['locale', 'tenant', scope], exact: true });
  void queryClient.invalidateQueries({ queryKey: ['ai', 'config', scope], exact: true });
  void queryClient.invalidateQueries({ queryKey: ['channels', 'default-reply', scope] });
}

/** Focus/reconnect must also pick up settings edited in another tab. */
function reconcileSessionPreferences(): void {
  invalidateTenantLanguage();
  const state = useAuthStore.getState();
  if (!state.token || !state.user?.id) return;
  void queryClient.invalidateQueries({ queryKey: currentChatChannelsKey() });
  void reconcileChatMessages(queryClient);
  void reconcileHostApps(queryClient);
  void reconcileNotifications(queryClient);
  void reconcileWAEvents(queryClient);
  void queryClient.invalidateQueries({ queryKey: crmListKey() });
  void queryClient.invalidateQueries({ queryKey: contactExportKey() });
  void queryClient.invalidateQueries({ queryKey: contactImportChannelsKey() });
  void queryClient.invalidateQueries({ queryKey: openConversationChannelsKey() });
  void queryClient.invalidateQueries({ queryKey: contactPurgeKey() });
  void queryClient.invalidateQueries({ predicate: query => isCurrentCRMContactKey(query.queryKey) });
  void queryClient.invalidateQueries({ predicate: query => isCurrentContactPipelineKey(query.queryKey) });
  void queryClient.invalidateQueries({ queryKey: notificationSettingsKey(), exact: true });
  void queryClient.invalidateQueries({ queryKey: tagLibraryKey(), exact: true });
  void queryClient.invalidateQueries({ queryKey: quickReplyLibraryKey() });
  void queryClient.invalidateQueries({ queryKey: macroLibraryKey() });
  void queryClient.invalidateQueries({ queryKey: customFieldLibraryKey() });
  void queryClient.invalidateQueries({ queryKey: ['settings', 'inbox-order', sessionScope(state)], exact: true });
  void queryClient.invalidateQueries({predicate:query=>currentContextKey(query.queryKey)});
  void queryClient.invalidateQueries({predicate:query=>currentMessageHistoryKey(query.queryKey)});
  void queryClient.invalidateQueries({ predicate: query => currentCustomerPanelKey(query.queryKey) });
}

/** Current local mirror of the server's assignment + channel-membership scope. */
function currentConversationVisibilityScope() {
  const permissions = usePermissionsStore.getState();
  const modules = queryClient.getQueryData<ModulesMap>(['me', 'modules']);
  const channels = queryClient.getQueryData<ChannelResponse[]>(['inbox', 'channels']);
  return {
    ...permissions,
    channelScopingEnabled:
      modules === undefined
        ? undefined
        : isModuleEnabled(modules, 'inbox_channel_scoping'),
    accessibleChannelIds: Array.isArray(channels)
      ? channels.flatMap((channel) => (channel.id ? [channel.id] : []))
      : undefined,
  };
}

/** Tenant-wide Inbox events may notify only conversations already in our list. */
function isConversationInVisibleLists(conversationId: string): boolean {
  return queryClient
    .getQueriesData({ queryKey: ['conversations'] })
    .some(([, cache]) => conversationCacheContains(cache, conversationId));
}

/**
 * Insert the complete message payload emitted by `inbox:new_message` into an
 * already-open conversation. A refetch alone can race the transaction that
 * persisted an inbound media message: the conversation preview is updated,
 * but the immediate messages request can still return the previous snapshot.
 * The event has enough data to render the message now; the next refetch still
 * reconciles the server-only fields.
 */
function upsertIncomingInboxMessage(
  conversationId: string,
  data?: Record<string, unknown>,
): boolean {
  type Msg = import('@/features/inbox/queries').Message;
  const normalized = normalizeInboxMessage(data);
  const messageId =
    typeof normalized.message_id === 'string'
      ? normalized.message_id
      : typeof normalized.id === 'string'
        ? normalized.id
        : undefined;
  if (!messageId) return false;

  const queryKey = ['messages', conversationId] as const;
  if (!Array.isArray(queryClient.getQueryData<unknown>(queryKey))) return false;

  const content = typeof normalized.content === 'string' ? normalized.content : '';
  const direction = typeof normalized.direction === 'string' ? normalized.direction : 'inbound';
  const messageType =
    typeof normalized.message_type === 'string' ? normalized.message_type : 'text';
  const serverCreatedAt = typeof normalized.created_at === 'string' && Number.isFinite(new Date(normalized.created_at).getTime())
    ? normalized.created_at : undefined;
  const createdAt = serverCreatedAt ?? new Date().toISOString();
  const senderName =
    typeof normalized.sender_name === 'string'
      ? normalized.sender_name
      : typeof normalized.user_name === 'string'
        ? normalized.user_name
        : undefined;
  const mediaUrl =
    typeof normalized.media_url === 'string' ? normalized.media_url : undefined;
  const mediaMimeType =
    typeof normalized.media_mime_type === 'string'
      ? normalized.media_mime_type
      : undefined;
  const mediaFileName =
    typeof normalized.media_file_name === 'string'
      ? normalized.media_file_name
      : undefined;
  const mediaCaption =
    typeof normalized.media_caption === 'string' ? normalized.media_caption : undefined;
  const mediaStatus =
    normalized.media_status === 'ready' ||
    normalized.media_status === 'pending' ||
    normalized.media_status === 'failed'
      ? normalized.media_status
      : undefined;
  const mediaSize =
    typeof normalized.media_size === 'number' ? normalized.media_size : undefined;
  const metadata =
    normalized.metadata && typeof normalized.metadata === 'object'
      ? normalized.metadata
      : undefined;

  const incoming = {
    id: messageId,
    conversation_id: conversationId,
    content,
    direction,
    message_type: messageType,
    // A raw/legacy duplicate retires prior presentation rather than guessing
    // that a matching body still carries native generated origin.
    template_presentation: readTemplatePresentation({ direction, message_type: messageType, content, template_presentation: normalized.template_presentation }),
    created_at: createdAt,
    ...(senderName ? { user_name: senderName } : {}),
    ...(mediaUrl ? { media_url: mediaUrl } : {}),
    ...(mediaMimeType ? { media_mime_type: mediaMimeType } : {}),
    ...(mediaFileName ? { media_file_name: mediaFileName } : {}),
    ...(mediaCaption ? { media_caption: mediaCaption } : {}),
    ...(mediaStatus ? { media_status: mediaStatus } : {}),
    ...(mediaSize !== undefined ? { media_size: mediaSize } : {}),
    ...(metadata ? { metadata } : {}),
  } as Msg;

  queryClient.setQueryData<Msg[]>(queryKey, (prev) => {
    if (!Array.isArray(prev)) return prev;
    const index = prev.findIndex((message) => message.id === messageId);
    if (index >= 0) {
      const next = [...prev];
      next[index] = {
        ...prev[index],
        ...incoming,
        // New producers supply authoritative creation time; it may correct an
        // earlier legacy event's provisional "now". Without it, keep the loaded
        // row's time so duplicate legacy events never move it to today.
        created_at: serverCreatedAt ?? prev[index].created_at ?? incoming.created_at,
      };
      return next;
    }
    return [...prev, incoming];
  });
  return true;
}

/** Replace an opaque provider media id with the final CDN URL in-place. */
function applyInboxMessageMediaReady(
  conversationId: string,
  data?: Record<string, unknown>,
): boolean {
  type Msg = import('@/features/inbox/queries').Message;
  const messageId = typeof data?.message_id === 'string' ? data.message_id : undefined;
  const mediaUrl = typeof data?.media_url === 'string' ? data.media_url : undefined;
  const mediaMimeType =
    typeof data?.media_mime_type === 'string' ? data.media_mime_type : undefined;
  const mediaSize = typeof data?.media_size === 'number' ? data.media_size : undefined;
  if (!messageId || !mediaUrl) return false;

  const queryKey = ['messages', conversationId] as const;
  if (!Array.isArray(queryClient.getQueryData<unknown>(queryKey))) return false;

  let changed = false;
  queryClient.setQueryData<Msg[]>(queryKey, (previous) => {
    if (!Array.isArray(previous)) return previous;
    return previous.map((message) => {
      if (message.id !== messageId) return message;
      changed = true;
      return {
        ...message,
        media_url: mediaUrl,
        media_status: 'ready',
        ...(mediaMimeType ? { media_mime_type: mediaMimeType } : {}),
        ...(mediaSize !== undefined ? { media_size: mediaSize } : {}),
      };
    });
  });
  return changed;
}

/**
 * Apply the surgical part of `inbox:message_deleted` immediately. The event
 * is broadcast to every agent in the tenant, so the operation must be safe
 * when the message is already absent (the actor's optimistic mutation and
 * the broadcast can both reach this cache).
 *
 * `last_message_preview` is intentionally patched only when the backend
 * sends a string. A missing/null preview means the server could not
 * recalculate it; blanking a still-valid list preview would be worse than a
 * short-lived stale value. Clear a projection identifying the deleted message
 * even without a recalculated string; the following list read selects the
 * actual latest live message. We also leave `last_message_at` untouched because
 * deleting a message must not reorder the conversation list.
 */
function removeDeletedInboxMessage(
  conversationId: string,
  data?: Record<string, unknown>,
): void {
  type Msg = import('@/features/inbox/queries').Message;
  const messageId =
    typeof data?.message_id === 'string'
      ? data.message_id
      : typeof data?.id === 'string'
        ? data.id
        : undefined;

  if (messageId) {
    queryClient.setQueryData<Msg[]>(['messages', conversationId], (prev) => {
      if (!Array.isArray(prev)) return prev;
      const next = prev.filter((message) => message.id !== messageId);
      return next.length === prev.length ? prev : next;
    });
  }

  const preview = data?.last_message_preview;
  if (typeof preview !== 'string' && !messageId) return;

  patchConversationCaches((list) => {
    let changed = false;
    const next = list.map((conversation) => {
      if (conversation.id !== conversationId) return conversation;
      if (typeof preview !== 'string' && conversation.last_message_event?.id !== messageId) return conversation;
      changed = true;
      // A deleted last message may have carried the structured event. Never
      // keep that stale label beside the newly recalculated literal preview.
      return { ...conversation, ...(typeof preview === 'string' ? { last_message_preview: preview } : {}), ...(conversation.last_message_event ? { last_message_event: null } : {}) };
    });
    return changed ? next : list;
  });
}

/**
 * Controlled-provider event for END-USER typing toward the agent, distinct
 * from `inbox:typing` (agent→agent). Meta does not expose this signal; the UI
 * consumes this lane only for webchat. Expected payload:
 *   { conversation_id, contact_id?, is_typing }
 */
export const CUSTOMER_TYPING_EVENT = 'inbox:customer_typing';

/** Postcommit repair hint; historical rows are read again, never new-message alerts. */
export const INBOX_HISTORY_RECONCILED_EVENT = 'inbox:history_reconciled';
export interface InboxHistoryReconciledEvent {
  type: typeof INBOX_HISTORY_RECONCILED_EVENT;
  data: { conversation_id: string };
  tenant_id?: string;
}

interface WSMessage {
  type: string;
  data?: Record<string, unknown> & {
    id?: string;
    conversation_id?: string;
    channel_id?: string;
    pipeline_id?: string;
    contact_id?: string;
    meeting_id?: string;
    call_id?: string;
    user_id?: string;
    parent_message_id?: string;
  };
  // Some events flatten the payload one level
  conversation_id?: string;
  channel_id?: string;
  user_id?: string;
  parent_message_id?: string;
  [key: string]: unknown;
}

class RealtimeManager {
  private connectionToken: string | null = null;
  private connectionRevision: number | null = null;
  private socket: Socket | null = null;
  private ws: WebSocket | null = null;
  private baseUrl: string;
  private reconnectAttempt = 0;
  private metricsTimer: ReturnType<typeof setTimeout> | null = null;
  private onVisibility = () => {
    if (document.visibilityState !== 'visible') return;
    reconcileSessionPreferences();
    void reconcilePlatformTransferQueries(queryClient);
    void queryClient.invalidateQueries({ queryKey: ['me', 'permissions'] });
    void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    void queryClient.invalidateQueries({ queryKey: ['messages'] });
    void reconcileCalls(queryClient).catch(() => {});
    void reconcileVisibleVoice(queryClient).catch(()=>{});
    reconcileAllDashboardReads(queryClient);
    this.scheduleMetrics();
  };
  private scheduleMetrics() {
    if (this.metricsTimer || document.visibilityState === 'hidden') return;
    const revision = sessionRevision();
    this.metricsTimer = setTimeout(() => {
      this.metricsTimer = null;
      if (revision !== sessionRevision()) return;
      void queryClient.invalidateQueries({ queryKey: ['reports'] });
      void queryClient.invalidateQueries({ queryKey: ['agents'] });
      void reconcileDashboardReads(queryClient, 'agents');
    }, 500);
  }
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  // Per-conversation last-emitted timestamp for inbox:typing throttle.
  // 2.5s window keeps the receiver's 4s TTL alive with margin to spare.
  private lastTypingEmit: Map<string, number> = new Map();
  // Per-channel last-emitted timestamp for the team-chat `typing` throttle
  // (same 2.5s window as the inbox lane).
  private lastChannelTypingEmit: Map<string, number> = new Map();
  // Socket.IO channel-room membership. The server only delivers `message.*`
  // to the room `channel:<id>` and auto-joins EXISTING memberships on
  // connect — channels created/joined while already connected need an
  // explicit `join_channel` emit. `joinedChannels` dedupes re-emits within a
  // socket session (cleared on disconnect: the reconnect re-runs the
  // server-side auto-join, and by then these runtime joins are persisted
  // memberships). `pendingJoins` queues joins requested while offline and is
  // flushed in the socket's `connect` handler.
  private joinedChannels: Set<string> = new Set();
  private pendingJoins: Set<string> = new Set();
  // Conversación de inbox que este agente está MIRANDO ahora (Canal 2 de
  // coordinación). Se sostiene aquí (no solo en el componente) para poder
  // re-emitir `join_conversation` tras un reconnect de Socket.IO — el server
  // no persiste viewers entre sesiones de socket.
  private viewingConversation: string | null = null;

  constructor() {
    // Bare origin for Socket.IO (which lives at `/socket.io`, outside `/api`).
    // Derived from the single API-prefix config point so REST and realtime can
    // never disagree on the host. See `src/api/api-config.ts`.
    this.baseUrl = apiOrigin();
  }

  connect() {
    const token = useAuthStore.getState().token;
    if (!token) return;
    if (this.connectionToken !== token || this.connectionRevision !== sessionRevision()) this.disconnect();
    this.connectionToken = token;
    this.connectionRevision = sessionRevision();

    document.addEventListener('visibilitychange', this.onVisibility);
    this.connectSocketIO(token);
    this.connectRawWS(token);
  }

  private connectSocketIO(token: string) {
    if (this.socket?.connected) return;

    this.socket = io(this.baseUrl, {
      auth: { token },
      // Prefer WS, fall back to polling. Some hosts (proxies, EasyPanel) drop
      // the WS upgrade silently and only polling works.
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 20,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
    });

    this.socket.on('connect', () => {
      reconcileSessionPreferences();
      void reconcilePlatformTransferQueries(queryClient);
      reconcileAllDashboardReads(queryClient);
      console.log(
        '[Realtime] Socket.IO connected (transport:',
        this.socket?.io?.engine?.transport?.name + ')',
      );
      // Permissions and inbox membership can change while this tab is offline.
      // Both keys have active observers in the app shell, so invalidation
      // immediately refetches and updates all fail-closed UI gates.
      void reconcileCalls(queryClient).catch(() => {});
    void reconcileVisibleVoice(queryClient).catch(()=>{});
      queryClient.invalidateQueries({ queryKey: ['me', 'permissions'] });
      queryClient.invalidateQueries({ queryKey: ['me', 'modules'] });
      queryClient.invalidateQueries({ queryKey: ['inbox', 'channels'] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['inbox', 'stats'] });
      // Flush channel joins requested while the socket was down. The server
      // already auto-joined our persisted memberships on connect, so these
      // are at worst idempotent no-ops (membership is validated server-side).
      for (const channelId of this.pendingJoins) {
        this.socket?.emit('join_channel', { channel_id: channelId });
        this.joinedChannels.add(channelId);
      }
      this.pendingJoins.clear();
      // Re-announce ourselves as viewer of the open conversation: the server
      // dropped us from its viewer set when the previous socket died, and
      // peers already received our `agent_left_viewing` on that disconnect.
      if (this.viewingConversation) {
        this.emitJoinConversation(this.viewingConversation);
      }
    });

    this.socket.on('connect_error', (err) => {
      console.error('[Realtime] Socket.IO connect_error:', err.message);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[Realtime] Socket.IO disconnected:', reason);
      // Reconnect re-runs the server-side auto-join of persisted memberships,
      // so the dedup set must reset — otherwise a runtime join after the next
      // reconnect would be wrongly skipped for a channel that changed state.
      this.joinedChannels.clear();
      // Viewer presence is ephemeral hub state: while offline we can't hear
      // `agent_left_viewing`, so anything kept would be ghosts. Drop it —
      // the post-reconnect list refetch re-seeds from the REST snapshot.
      useInboxPresenceStore.getState().clear();
    });

    // Backend emits business events (inbox:new_message, channel.created, etc.)
    // by event NAME on Socket.IO — not as a typed envelope. Catch all events
    // with onAny() and route them through the same handler the raw WS uses.
    // Without this, only the few events we hardcoded would ever update the UI.
    const socketRevision=sessionRevision();
    this.socket.onAny((eventName: string, payload?: unknown) => {
      if(socketRevision!==sessionRevision())return;
      let message: WSMessage;
      if (payload && typeof payload === 'object' && 'type' in payload && (!(eventName.startsWith('call:') || eventName.startsWith('voice:') || eventName === 'meeting:recording_changed') || (payload.type === eventName && 'data' in payload))) {
        // Backend already wrapped it ({ type, data, ... }) — use as-is
        message = payload as WSMessage;
      } else {
        // Raw payload — synthesize the envelope so handleWSMessage can branch
        message = {
          type: eventName,
          data: (payload ?? {}) as WSMessage['data'],
        };
      }
      this.handleWSMessage(message);
    });
  }

  private connectRawWS(token: string) {
    if (this.ws?.readyState === WebSocket.OPEN) return;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    const wsUrl = rawWsUrl(token);
    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      reconcileSessionPreferences();
      void reconcilePlatformTransferQueries(queryClient);
      reconcileAllDashboardReads(queryClient);
      console.log('[Realtime] Raw WS connected');
      this.reconnectAttempt = 0;
      void queryClient.invalidateQueries({ queryKey: ['me', 'permissions'] });
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      void queryClient.invalidateQueries({ queryKey: ['messages'] });
      void reconcileCalls(queryClient).catch(() => {});
    void reconcileVisibleVoice(queryClient).catch(()=>{});
      void queryClient.invalidateQueries({ queryKey: ['reports'] });
    };

    const rawRevision=sessionRevision();
    this.ws.onmessage = (event) => {
      if(rawRevision!==sessionRevision())return;
      try {
        const message = JSON.parse(event.data) as WSMessage;
        this.handleWSMessage(message);
      } catch (e) {
        console.error('[Realtime] Failed to parse WS message', e);
      }
    };

    this.ws.onclose = () => {
      console.log('[Realtime] Raw WS disconnected');
      this.reconnectTimeout = setTimeout(() => {
        const newToken = useAuthStore.getState().token;
        if (newToken) this.connectRawWS(newToken);
      }, Math.min(30_000, 1000 * 2 ** Math.min(this.reconnectAttempt++, 5)) * (0.75 + Math.random() * 0.5));
    };

    this.ws.onerror = (err) => {
      console.error('[Realtime] Raw WS error', err);
    };
  }

  /**
   * Join the Socket.IO room `channel:<id>` so `message.*` broadcasts for a
   * channel we became a member of AFTER connect actually reach this client
   * (the server only auto-joins memberships that existed at connect time).
   * The server validates membership on `join_channel`, so emitting is cheap
   * and idempotent; deduped per socket session via `joinedChannels` and
   * queued in `pendingJoins` while offline.
   */
  joinChannel(channelId: string) {
    if (!channelId || this.joinedChannels.has(channelId)) return;
    if (this.socket?.connected) {
      this.socket.emit('join_channel', { channel_id: channelId });
      this.joinedChannels.add(channelId);
    } else {
      this.pendingJoins.add(channelId);
    }
  }

  /** Leave the channel room (mirrors `joinChannel`) and forget it locally. */
  leaveChannel(channelId: string) {
    if (!channelId) return;
    this.joinedChannels.delete(channelId);
    this.pendingJoins.delete(channelId);
    if (this.socket?.connected) {
      this.socket.emit('leave_channel', { channel_id: channelId });
    }
  }

  /**
   * Announce this agent as VIEWER of an inbox conversation (Canal 2 de
   * coordinación). The server validates tenant, adds us to its viewer set and
   * broadcasts `inbox:agent_viewing` to the rest of the tenant. Call on
   * conversation open; switching conversations should `leaveConversation`
   * the previous one first. Safe to call while offline — the id is retained
   * and re-announced by the `connect` handler.
   */
  joinConversation(conversationId: string) {
    if (!conversationId || this.viewingConversation === conversationId) return;
    this.viewingConversation = conversationId;
    this.emitJoinConversation(conversationId);
  }

  /** Stop being a viewer (mirrors `joinConversation`). */
  leaveConversation(conversationId: string) {
    if (!conversationId) return;
    if (this.viewingConversation === conversationId) {
      this.viewingConversation = null;
    }
    if (this.socket?.connected) {
      this.socket.emit('leave_conversation', { conversation_id: conversationId });
    }
  }

  private emitJoinConversation(conversationId: string) {
    if (!this.socket?.connected) return;
    const user = useAuthStore.getState().user;
    // Gotcha #10 de CONTRACT-realtime: el server NO conoce nombre/avatar del
    // socket — si no viajan en el payload, el broadcast `inbox:agent_viewing`
    // sale con campos vacíos y los peers muestran un viewer anónimo.
    this.socket.emit('join_conversation', {
      conversation_id: conversationId,
      agent_name: user?.name ?? '',
      agent_avatar: user?.avatar ?? '',
    });
  }

  /**
   * Map WS event types to query-cache invalidations.
   *
   * Convention: prefer invalidating the most specific list/key first, then any
   * derived stat/badge queries. The handler is intentionally permissive about
   * payload shape — different events emit slightly different envelopes.
   */
  private handleWSMessage(message: WSMessage) {
    if (message.type === INBOX_HISTORY_RECONCILED_EVENT) {
      const state = useAuthStore.getState();
      const tenant = sessionTenantId(state);
      const id = message.data?.conversation_id;
      // Socket.IO may supply data alone on its authenticated tenant connection.
      // Every declared tenant must agree; IDs never supply cache contents.
      if (!state.token || !tenant ||
          [message.tenant_id, message.data?.tenant_id].some(value => value !== undefined && value !== tenant) ||
          typeof id !== 'string' || !/^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(id) ||
          id === '00000000-0000-0000-0000-000000000000') return;
      void queryClient.invalidateQueries({ queryKey: ['messages', id] });
      void queryClient.invalidateQueries({ queryKey: ['conversation', id] });
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      void queryClient.invalidateQueries({ queryKey: ['inbox', 'stats'] });
      return;
    }
    if (message.type.startsWith('inbox:') || message.type === 'metrics.invalidated') this.scheduleMetrics();
    const { type, data } = message;
    if (!type) return;
    if (channelHintTypes.has(type) && !ownedChannelHint(message, { tenant: sessionTenantId(useAuthStore.getState()), user: useAuthStore.getState().user?.id })) return;

    // Conversation IDs may live on `data` or be flattened to the top
    const inboxMessagePayload = extractInboxMessagePayload(data);
    const conversationId = (
      data?.conversation_id ??
      inboxMessagePayload.conversation_id ??
      message.conversation_id
    ) as string | undefined;
    const channelId = (data?.channel_id ?? message.channel_id) as string | undefined;
    const pipelineId = data?.pipeline_id as string | undefined;
    const meetingId = data?.meeting_id as string | undefined;
    // For team-chat thread replies, the broadcast carries the parent's id —
    // we use it to invalidate the open thread query so the new reply lands
    // and the parent's reply_count badge bumps.
    const parentMessageId = (data?.parent_message_id ?? message.parent_message_id) as
      | string
      | undefined;
    // Internal native hints are handled before Inbox paths. Unknown/foreign
    // v1 payloads have no effects; legacy channel producers may request GETs
    // for a known owned channel, never patch history or manufacture alerts.
    if(messageHintTypes.has(type) && (data?.version!==undefined || channelId&&!conversationId)) {
      const owner=captureChatOwner(),hint=ownedMessageHint(message,owner);
      if(hint){
        void reconcileChatMessages(queryClient,hint.channelId,hint.parentMessageId??hint.messageId,owner);
        void queryClient.invalidateQueries({queryKey:currentChatChannelsKey()});
        if(hint.type==='message.new'||hint.type==='message.mention')void this.notifyOwnedChatHint(hint,owner);
      } else if(data?.version===undefined&&channelId&&currentChatChannel(queryClient,channelId)&&
        [message.tenant_id,data?.tenant_id].every(value=>value===undefined||value===owner.tenant)) {
        void reconcileChatMessages(queryClient,channelId,undefined,owner);
        void queryClient.invalidateQueries({queryKey:currentChatChannelsKey()});
      }
      return;
    }
    if(type.startsWith('thread:')) {
      const owner=captureChatOwner(),hint=ownedTrackingHint(message,owner);
      if(hint){
        void reconcileChatMessages(queryClient,hint.channelId,hint.parentId,owner);
        if(hint.type!=='thread:tracking_updated')void this.notifyOwnedTrackingHint(hint,owner);
      } else if(data?.version===undefined&&trackingHintTypes.has(type)&&channelId&&currentChatChannel(queryClient,channelId)&&[message.tenant_id,data?.tenant_id].every(value=>value===undefined||value===owner.tenant))
        void reconcileChatMessages(queryClient,channelId,undefined,owner);
      return;
    }
    const isNewMessageEvent = type === 'inbox:new_message' || type === 'message.new';

    // Customer (end-user) typing indicator. Handled before the switch so the
    // event name lives in exactly one constant for easy backend coordination.
    // `is_typing` defaults to true when omitted; an explicit `false` clears.
    if (type === CUSTOMER_TYPING_EVENT) {
      if (conversationId) {
        const isTyping = data?.is_typing !== false;
        useTypingStore.getState().setCustomerTyping(conversationId, isTyping);
      }
      return;
    }

    // Contact hints from an authenticated connection may omit legacy tenant
    // metadata. If present, every owner pointer must match this session.
    if (['inbox:contact_created', 'inbox:contact_updated', 'inbox:contact_deleted'].includes(type)) {
      const tenant = sessionTenantId(useAuthStore.getState());
      const nested = data?.contact;
      const nestedTenant = nested && typeof nested === 'object' && 'tenant_id' in nested ? nested.tenant_id : undefined;
      if (!tenant || [message.tenant_id, data?.tenant_id, nestedTenant].some(owner => owner !== undefined && owner !== tenant)) return;
    }

    if (type.startsWith('kanban:')) {
      const tenant = sessionTenantId(useAuthStore.getState());
      if (!tenant || [message.tenant_id, data?.tenant_id].some(owner => owner !== undefined && owner !== tenant)) return;
      if (['kanban:pipeline_changed', 'kanban:stage_changed', 'kanban:card_moved', 'kanban:card_created', 'kanban:card_updated', 'kanban:card_deleted'].includes(type)) {
        void reconcileDashboardReads(queryClient, 'pipeline');
      }
    }


    switch (type) {
      case 'notification:new':
      case 'notification:read':
      case 'notification:deleted':
        // Directed native envelopes serialize their unset UUID as all zeros.
        // Data still must declare this exact recipient and tenant.
        if(message.tenant_id!==undefined&&message.tenant_id!=='00000000-0000-0000-0000-000000000000'&&message.tenant_id!==sessionTenantId(useAuthStore.getState()))break;
        receiveNotificationHint(queryClient, type, data);
        break;
      case 'inbox:contacts_purged': {
        const tenant = sessionTenantId(useAuthStore.getState());
        if (!tenant || data?.tenant_id !== tenant || message.tenant_id !== undefined && message.tenant_id !== tenant ||
            !isContactPurgeId(data.request_id) || typeof data.contacts_deleted !== 'number' || !Number.isSafeInteger(data.contacts_deleted) || data.contacts_deleted < 0) return;
        // Zero also consumes a challenge. Reread instead of clearing rows:
        // new contacts may legitimately exist after the deletion snapshot.
        void queryClient.invalidateQueries({ predicate: query => currentContactList(query.queryKey) });
        void queryClient.invalidateQueries({ predicate: query => currentContactMetadata(query.queryKey) });
        void queryClient.invalidateQueries({ predicate: query => isCurrentContactPipelineKey(query.queryKey) });
        void queryClient.invalidateQueries({ queryKey: [...contactPurgeKey(), data.request_id] });
        void queryClient.invalidateQueries({ queryKey: ['conversations'] });
        break;
      }
      case 'inbox:contacts_imported': {
        const tenant = sessionTenantId(useAuthStore.getState());
        if (!tenant || data?.tenant_id !== tenant || message.tenant_id !== undefined && message.tenant_id !== tenant ||
            !['total', 'imported', 'updated'].every(key => typeof data[key] === 'number' && Number.isSafeInteger(data[key]) && Number(data[key]) >= 0) ||
            Number(data.imported) + Number(data.updated) <= 0 || Number(data.imported) + Number(data.updated) > Number(data.total)) return;
        // Committed aggregate hints are not deltas to apply to cached counts.
        // A pre-import read may still be running. Restart it so its old snapshot
        // cannot consume this one terminal hint and leave the tab stale.
        void queryClient.invalidateQueries({ predicate: query => currentContactList(query.queryKey) });
        void queryClient.invalidateQueries({ predicate: query => currentContactMetadata(query.queryKey) });
        void queryClient.invalidateQueries({ queryKey: tagLibraryKey() });
        void queryClient.invalidateQueries({ queryKey: ['conversations'] });
        break;
      }
      case 'inbox:contact_export_changed': {
        const tenant = sessionTenantId(useAuthStore.getState());
        if (!tenant || data?.tenant_id !== tenant || message.tenant_id !== undefined && message.tenant_id !== tenant || typeof data.id !== 'string' || !data.id || !['pending', 'running', 'done', 'failed'].includes(String(data.status))) return;
        void queryClient.invalidateQueries({ queryKey: contactExportKey() }, { cancelRefetch: false });
        break;
      }
      case 'tenant.language_changed': {
        const owner = sessionTenantId(useAuthStore.getState());
        if (owner && data?.tenant_id === owner && typeof data.language === 'string' && normalizeLocale(data.language) === data.language &&
            (message.tenant_id === undefined || message.tenant_id === owner)) {
          invalidateTenantLanguage();
        }
        break;
      }
      case 'apps.catalog_changed': {
        if (ownedAppCatalogHint(data, message.tenant_id)) void reconcileHostApps(queryClient);
        break;
      }
      // Private plugin hint (initiating user's tenant room): IDs only, re-read by GET.
      case 'plugins:changed': {
        const hint = ownedPluginHint(message);
        if (hint) void reconcilePluginHint(queryClient, hint);
        break;
      }
      // ===== Customer Inbox =====
      case 'inbox:message_media_ready':
        if ([message.tenant_id, data?.tenant_id].some(owner => owner !== undefined && owner !== sessionTenantId(useAuthStore.getState()))) break;
        if (conversationId) useAiSuggestionStore.getState().observeInteraction(conversationId, undefined, true);
        if (conversationId && !applyInboxMessageMediaReady(conversationId, data)) {
          // Cold/missing message caches have nothing to patch; reconcile from
          // REST when the thread is active instead of manufacturing a row.
          queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });
        }
        break;

      case 'inbox:new_message':
      case 'inbox:message_deleted':
      case 'inbox:message_edited':
      case 'message.new':
      case 'message.edit':
      case 'message.delete':
        if (conversationId && [message.tenant_id, data?.tenant_id, inboxMessagePayload.tenant_id].some(owner => owner !== undefined && owner !== sessionTenantId(useAuthStore.getState()))) break;
        if (type === 'inbox:new_message' && conversationId) void reconcilePlatformTransferQueries(queryClient, conversationId);
        // `message.*` (without an `inbox:` prefix) is broadcast for BOTH the
        // customer inbox AND the internal team-chat. We don't know which one
        // up-front, so invalidate both worlds — TanStack Query no-ops the
        // refetch for any key that has no observers, so this is cheap.
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
        if (type === 'inbox:message_edited' || type === 'inbox:message_deleted') {
          const ownerTenant = sessionTenantId(useAuthStore.getState());
          if ([data?.tenant_id, message.tenant_id].every(declared => declared === undefined || declared === ownerTenant)) {
            // Payload is a hint only: native GET rechecks current visibility.
            const messageId = data?.message_id ?? data?.id;
            void queryClient.invalidateQueries({ predicate: query => currentMessageHistoryKey(query.queryKey) && (typeof messageId !== 'string' || query.queryKey[1] === messageId) });
          }
        }
        if (conversationId) {
          if ([message.tenant_id, data?.tenant_id, inboxMessagePayload.tenant_id].every(owner => owner === undefined || owner === sessionTenantId(useAuthStore.getState()))) {
            const id = inboxMessagePayload.message_id ?? inboxMessagePayload.id ?? data?.message_id ?? data?.id;
            useAiSuggestionStore.getState().observeInteraction(conversationId, typeof id === 'string' ? id : undefined, !isNewMessageEvent);
          }
          reconcileCRMActivity(conversationId, data, message.tenant_id);
          if (type === 'inbox:message_deleted') {
            removeDeletedInboxMessage(conversationId, data);
          }
          // New-message events contain the media fields needed by the open
          // chat. Upsert first so an immediate refetch cannot lose an audio
          // note while the backend transaction is still becoming visible.
          const didUpsert = isNewMessageEvent
            ? upsertIncomingInboxMessage(conversationId, data)
            : false;
          if (!didUpsert) {
            queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });
          }
          queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
        }
        queryClient.invalidateQueries({ queryKey: ['inbox', 'stats'] });
        if (channelId) {
          queryClient.invalidateQueries({ queryKey: ['team-chat', 'channels'] });
          queryClient.invalidateQueries({ queryKey: messageKey('messages',channelId) });
          // Q1: `message.new` now carries parent_message_id (MessageDTO
          // .ToWSPayload — verified in message_dto.go), so thread replies get
          // the SURGICAL per-parent invalidation below; the old TC-5 broad
          // fallback is retired for new-message events. The edit/delete
          // broadcasts still build minimal payloads WITHOUT
          // parent_message_id ({id, channel_id, content?, …} — see
          // message_handler.go UpdateMessage/DeleteMessage), so for those we
          // keep the broad active-only invalidation (at most the one open
          // thread panel refetches). FORWARD-COMPAT: the backend offered to
          // enrich edit/delete with parent_message_id too (Matrix #90159) —
          // when that lands, `parentMessageId` will be set here and we skip
          // the broad path automatically, zero FE changes needed.
          if (type !== 'message.new' && type !== 'inbox:new_message' && !parentMessageId) {
            queryClient.invalidateQueries({
              predicate: query => query.queryKey[1]==='thread' && ownsMessageKey(query.queryKey),
              refetchType: 'active',
            });
          }
        }
        if (parentMessageId) {
          queryClient.invalidateQueries({ queryKey: messageKey('thread',parentMessageId) });
        }
        // Notify (sound + desktop) only on actual NEW messages, not on
        // edit/delete/status events that share this case statement.
        if (type === 'inbox:new_message' || type === 'message.new') {
          // Most broadcasts carry the message inside `data`, but Socket.IO and
          // older deployments can flatten identity fields onto the envelope.
          // Merge both so a valid DM is not discarded for "missing" user_id.
          const notificationData = {
            ...data,
            user_id: data?.user_id ?? message.user_id,
            channel_id: data?.channel_id ?? message.channel_id,
          };
          this.maybeNotifyOnNewMessage(notificationData, conversationId, channelId);
        }
        break;

      // ===== Outbound delivery receipts (ticks) =====
      // `inbox:message_status` reports an OUTBOUND message moving along
      // pending → sent → delivered → read (or → failed). The payload is
      // per-message, so we patch ONLY the matching row in the messages cache
      // — no list/stats invalidation (a delivery receipt doesn't change
      // ordering or unread counts). Same surgical pattern as
      // `inbox:audio_transcript` below.
      case 'inbox:message_status': {
        if (!conversationId) break;
        const messageId = data?.message_id as string | undefined;
        const externalId = data?.external_id as string | undefined;
        const nextStatus = data?.status as import('@blazeteams/sdk').MessageStatus | undefined;
        // Without an id we can't target a row. In DEV, surface the malformed
        // event rather than dropping a delivery receipt silently.
        if (!messageId && !externalId) {
          if (import.meta.env.DEV) {
            console.warn('[Realtime] inbox:message_status without message_id/external_id', data);
          }
          break;
        }
        // An unrecognised status (backend typo, or a state the SDK enum hasn't
        // learned yet) can't advance the message — drop it, but make the cause
        // visible in DEV instead of failing mute.
        if (nextStatus && !isKnownStatus(nextStatus) && import.meta.env.DEV) {
          console.warn('[Realtime] inbox:message_status with unknown status', nextStatus, data);
        }
        type Msg = import('@/features/inbox/queries').Message;
        queryClient.setQueryData<Msg[]>(['messages', conversationId], (prev) => {
          if (!Array.isArray(prev)) return prev;
          let mutated = false;
          const next = prev.map((m) => {
            const matches =
              (!!messageId && m.id === messageId) ||
              (!!externalId && m.external_id === externalId);
            // Delivery receipts only apply to OUTBOUND messages — never mutate
            // an inbound row even if a stray event targets its id.
            if (!matches || m.direction !== 'outbound') return m;
            // Monotonía: only apply a status that moves the message forward,
            // so an out-of-order `delivered` webhook can't undo a `read`.
            if (!isStatusAdvance(m.status, nextStatus)) return m;
            mutated = true;
            return {
              ...m,
              status: nextStatus,
              // omitempty means the event may carry only the timestamp that
              // just became true — keep any earlier ones we already have.
              delivered_at: (data?.delivered_at as string | undefined) ?? m.delivered_at,
              read_at: (data?.read_at as string | undefined) ?? m.read_at,
              // Capture the failure reason from whichever field the backend
              // populates on the status event (`error` per the WS struct, but
              // accept `failed_reason`/`reason` defensively). The tooltip on the
              // "Falló" badge surfaces this on hover.
              ...(nextStatus === 'failed'
                ? (() => {
                    const r =
                      (typeof data?.error === 'string' && data.error) ||
                      (typeof data?.failed_reason === 'string' && data.failed_reason) ||
                      (typeof data?.reason === 'string' && data.reason) ||
                      undefined;
                    return {
                      ...(r ? { failed_reason: r } : {}),
                      // A later raw/legacy failure retires earlier generated
                      // copy, even when its reason happens to have the same text.
                      failure_presentation: readDeliveryFailurePresentation(data?.failure_presentation, r),
                    };
                  })()
                : {}),
            };
          });
          // Return the same reference when nothing changed so React Query
          // doesn't notify observers for a no-op (stale/duplicate event).
          return mutated ? next : prev;
        });
        break;
      }

      case 'inbox:typing': {
        // Other agent is composing in a conversation. Backend enriches with
        // user_id/user_name (events.go:271-279). Drop our own echo (the
        // backend doesn't filter the sender out reliably across all
        // transports) and write to the typing store — the indicator
        // expires on its own after 4s.
        const userId = (data?.user_id ?? message.user_id) as string | undefined;
        const userName = (data?.user_name ?? message.user_name) as string | undefined;
        const selfId = useAuthStore.getState().user?.id;
        if (conversationId && userId && userName && userId !== selfId) {
          useTypingStore.getState().setTyping(conversationId, { userId, userName });
        }
        break;
      }

      case 'inbox:agent_viewing':
      case 'inbox:agent_left_viewing': {
        // Canal 2 de coordinación (spec 2026-07-09): otro agente abrió/cerró
        // una conversación. Escritura quirúrgica al presence store — NADA de
        // invalidar ['conversations'] acá: estos eventos son tenant-wide y
        // frecuentes (cada apertura de chat de cada agente), refetchear la
        // lista entera por cada uno era puro desperdicio. El payload es
        // AgentViewingEvent {conversation_id, agent_id, agent_name,
        // agent_avatar?, is_viewing} — branch por el FLAG (no por el nombre
        // del evento) para ser robustos si un build emite el mismo type con
        // is_viewing:false.
        const agentId = data?.agent_id as string | undefined;
        if (!conversationId || !agentId) break;
        const isViewing =
          typeof data?.is_viewing === 'boolean'
            ? (data.is_viewing as boolean)
            : type === 'inbox:agent_viewing';
        if (isViewing) {
          useInboxPresenceStore.getState().addViewer(
            conversationId,
            {
              userId: agentId,
              // `||` a propósito (no `??`): si el emisor no mandó
              // agent_name en join_conversation, el server propaga "" —
              // gotcha #10 — y un viewer sin nombre rompe el badge.
              name: (data?.agent_name as string | undefined) || 'Agente',
              avatar: (data?.agent_avatar as string | undefined) || undefined,
            },
            useAuthStore.getState().user?.id,
          );
        } else {
          useInboxPresenceStore.getState().removeViewer(conversationId, agentId);
          // Higiene del spec (resp. #2): quien dejó de mirar tampoco puede
          // seguir figurando como "escribiendo" — su typing muere con él en
          // vez de esperar el TTL de 4s.
          useTypingStore.getState().clearUser(conversationId, agentId);
        }
        break;
      }

      case 'inbox:agent_online':
      case 'inbox:agent_offline': {
        // Inbox-scoped presence signal — write directly into the presence
        // store so every consumer (assign dropdown, transfer modal, dashboard)
        // updates without refetching.
        const userId = (data?.user_id ?? message.user_id) as string | undefined;
        if (userId) {
          usePresenceStore.getState().setPresence(
            userId,
            type === 'inbox:agent_online' ? 'online' : 'offline',
          );
        }
        break;
      }

      case 'inbox:assigned':
      case 'inbox:unassigned': {
        // AssignmentEvent carries the new assignment state in two pointers:
        // the assigned USER and the assigned TEAM. The user pointer arrives as
        // `assigned_to_id` (older builds emitted `assigned_user_id`) — read
        // both so a bot routing to a team (assigned_to_id: null, assigned_team_id
        // set) and a manual agent-assign are both handled. The backend uses
        // omitempty so cleared values come through as `undefined` (NOT null) —
        // we mirror that on the cache so consumers can rely on
        // `value === undefined` checks.
        if (conversationId) {
          void reconcilePlatformTransferQueries(queryClient, conversationId);
          const userId =
            type === 'inbox:unassigned'
              ? undefined
              : ((data?.assigned_to_id ?? data?.assigned_user_id) as string | undefined);
          const teamId =
            type === 'inbox:unassigned'
              ? undefined
              : (data?.assigned_team_id as string | undefined);
          type Conv = import('@blazeteams/sdk').ConversationResponse;
          const patch = (c: Conv): Conv => ({
            ...c,
            assigned_user_id: userId,
            assigned_team_id: teamId,
          });
          // UPSERT the conversation lists. The assignment broadcast goes to the
          // WHOLE tenant, so we decide locally whether this conversation is
          // still ours to show, using the same visibility model the backend
          // scopes the list by. If it just LEFT our scope (e.g. routed to a
          // team we don't belong to, or unassigned from us), drop it immediately
          // instead of waiting for the refetch. Strict assignment means an
          // unassigned row stays visible only to `view_all`. If it's still ours,
          // patch it in place. A conversation that just ENTERED our scope isn't
          // in the cached list yet (nothing to patch) — the invalidate below
          // re-adds it via a server refetch, which is also the reconciler for
          // per-tab membership the optimistic patch can't recompute.
          const scope = currentConversationVisibilityScope();
          const previousDetail = queryClient.getQueryData<Conv>([
            'conversation',
            conversationId,
          ]);
          if (previousDetail) {
            const nextDetail = patch(previousDetail);
            if (isConversationVisible(nextDetail, scope)) {
              queryClient.setQueryData<Conv>(
                ['conversation', conversationId],
                nextDetail,
              );
            } else {
              queryClient.removeQueries({
                queryKey: ['conversation', conversationId],
                exact: true,
              });
            }
          }
          patchConversationCaches((list) =>
            list.flatMap((conversation) => {
              if (conversation.id !== conversationId) return [conversation];
              const next = patch(conversation);
              return isConversationVisible(next, scope) ? [next] : [];
            }),
          );
          // Still invalidate so the next refetch reconciles any field the
          // event didn't carry (priority, tags, etc.) and adds newly-visible rows.
          queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
        }
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
        queryClient.invalidateQueries({ queryKey: ['inbox', 'stats'] });
        break;
      }

      case 'inbox:note': {
        const tenant = sessionTenantId(useAuthStore.getState());
        if (conversationId && tenant && [message.tenant_id, data?.tenant_id].every(value => value === undefined || value === tenant)) {
          void reconcilePanelQueries(key => key[0] === 'conversation' && key[1] === conversationId);
        }
        break;
      }

      case 'inbox:conversation_tags_updated': {
        // Auto-tagging from BlazeAI gateway. Backend pushes:
        //   { conversation_id, tags: string[], source: 'ai_auto_analyze',
        //     sentiment, is_urgent, intent }
        // Optimistically write the new tag list (and bump priority if
        // is_urgent) into the conversation cache so the badge appears
        // instantly, then invalidate to pick up any other backend changes.
        if (!conversationId) break;
        const newTags = Array.isArray(data?.tags) ? (data.tags as string[]) : undefined;
        const isUrgent = data?.is_urgent === true;
        type Conv = import('@blazeteams/sdk').ConversationResponse;
        const patch = (c: Conv): Conv => ({
          ...c,
          ...(newTags ? { tags: newTags } : {}),
          ...(isUrgent ? { priority: 'urgent' as const } : {}),
        });
        queryClient.setQueryData<Conv>(
          ['conversation', conversationId],
          (prev) => (prev ? patch(prev) : prev),
        );
        patchConversationCaches((list) =>
          list.map((c) => (c.id === conversationId ? patch(c) : c)),
        );
        queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
        break;
      }

      // `inbox:conversation_update` also carries SNOOZE transitions (#94376):
      // data = { conversation_id, field: 'snoozed_until', new_value, is_snoozed }.
      // Posponer → is_snoozed=true; reactivar → new_value=null, is_snoozed=false.
      // The `['conversations']` prefix invalidation below refetches EVERY loaded
      // list variant (flat, infinite pages AND the `snoozed` tab), so the row
      // leaves/enters the active tabs from the server truth — the server already
      // excludes snoozed rows from the active tabs, no client patching needed.
      case 'inbox:new_conversation':
      case 'inbox:conversation_read':
        reconcileCRMActivity(conversationId, data, message.tenant_id);
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
        if (conversationId) {
          queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
        }
        queryClient.invalidateQueries({ queryKey: ['inbox', 'stats'] });
        break;

      case 'inbox:conversation_update': {
        reconcileCRMActivity(conversationId, data, message.tenant_id);
        if (conversationId) void reconcilePlatformTransferQueries(queryClient, conversationId);
        // Final tags contract sends the complete array in data.tags. Patch the
        // open detail and every loaded list page before the reconciliation
        // fetch so right-click/bulk changes are visible in the same frame.
        if (conversationId && data?.field === 'tags' && Array.isArray(data.tags)) {
          const tags = data.tags.filter((tag): tag is string => typeof tag === 'string');
          type Conv = import('@blazeteams/sdk').ConversationResponse;
          queryClient.setQueryData<Conv>(['conversation', conversationId], (previous) =>
            previous ? { ...previous, tags } : previous,
          );
          patchConversationCaches((conversations) =>
            conversations.map((conversation) =>
              conversation.id === conversationId ? { ...conversation, tags } : conversation,
            ),
          );
        }
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
        if (conversationId) {
          queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
        }
        queryClient.invalidateQueries({ queryKey: ['inbox', 'stats'] });
        break;
      }

      case 'custom_fields.catalog_changed': {
        const tenant = sessionTenantId(useAuthStore.getState());
        if (!tenant || data?.tenant_id !== tenant || (message.tenant_id !== undefined && message.tenant_id !== tenant) || (data?.entity_type !== 'contact' && data?.entity_type !== 'deal')) break;
        void queryClient.invalidateQueries({ queryKey: customFieldLibraryKey(data.entity_type) });
        break;
      }

      case 'inbox:macro_created':
      case 'inbox:macro_updated':
      case 'inbox:macro_deleted': {
        const tenant = sessionTenantId(useAuthStore.getState());
        if (!tenant || data?.tenant_id !== tenant || (message.tenant_id !== undefined && message.tenant_id !== tenant) || typeof data.id !== 'string' || !data.id) break;
        void queryClient.invalidateQueries({ queryKey: macroLibraryKey() });
        break;
      }

      case 'inbox:quick_reply_created':
      case 'inbox:quick_reply_updated':
      case 'inbox:quick_reply_deleted': {
        const tenant = sessionTenantId(useAuthStore.getState());
        if (!tenant || data?.tenant_id !== tenant || (message.tenant_id !== undefined && message.tenant_id !== tenant) || typeof data.id !== 'string' || !data.id) break;
        // Hints contain no template text. Reread the active/all lists only for
        // this session, preserving any editor draft during reconciliation.
        void queryClient.invalidateQueries({ queryKey: quickReplyLibraryKey() });
        break;
      }

      // Library events update only the current session's library. Deleting a
      // library entry preserves customer/conversation names (backend contract).
      case 'inbox:tag_created':
      case 'inbox:tag_updated':
      case 'inbox:tag_deleted': {
        const tenant = sessionTenantId(useAuthStore.getState());
        if (!tenant || data?.tenant_id !== tenant || (message.tenant_id !== undefined && message.tenant_id !== tenant) || typeof data.id !== 'string' || !data.id) break;
        const key = tagLibraryKey();
        if (type === 'inbox:tag_deleted') {
          queryClient.setQueryData<TagResponse[]>(key, previous => Array.isArray(previous) ? previous.filter(tag => tag.id !== data.id) : previous);
        } else {
          if (typeof data.name !== 'string' || !data.name.trim() || typeof data.color !== 'string') break;
          const tag: TagResponse = { id: data.id, tenant_id: tenant, name: data.name, color: data.color,
            ...(typeof data.icon === 'string' ? { icon: data.icon } : {}),
            ...(typeof data.updated_at === 'string' ? { updated_at: data.updated_at } : {}),
          };
          queryClient.setQueryData<TagResponse[]>(key, previous => {
            if (!Array.isArray(previous)) return previous;
            return previous.some(existing => existing.id === tag.id)
              ? previous.map(existing => existing.id === tag.id ? { ...existing, ...tag } : existing)
              : [...previous, tag];
          });
        }
        void queryClient.invalidateQueries({ queryKey: key, exact: true });
        break;
      }

      // ===== Contact CRUD =====
      // Contacts are embedded in conversation cards (name, avatar, phone)
      // so contact mutations need to reach BOTH the contact-detail caches
      // (DetailsTab, ContactDetail page) AND the conversation list/header
      // — otherwise an agent renaming a customer in CRM wouldn't see the
      // change in inbox until next manual refresh.
      case 'inbox:contact_created':
        void reconcileContactLists();
        break;

      case 'inbox:contact_updated': {
        type Contact = import('@blazeteams/sdk').ContactResponse;
        const nestedContact =
          data?.contact && typeof data.contact === 'object'
            ? (data.contact as Partial<Contact>)
            : undefined;
        const contactId = (
          nestedContact?.id ?? data?.id ?? data?.contact_id ?? message.contact_id
        ) as string | undefined;
        if (contactId) {
          // Current backend emits the changed contact as data.* with `id`.
          // Accept `contact_id` and data.contact as defensive aliases used by
          // older/generic event serializers, while keeping one canonical patch.
          const { contact: _contact, contact_id: _contactId, ...flatData } = data ?? {};
          const contactPatch = {
            ...flatData,
            ...nestedContact,
            id: contactId,
          } as Partial<Contact>;
          // Merge the event payload into the cached contact so we keep
          // any fields the backend didn't include in the delta (older
          // builds shipped only the changed keys). If the cache is cold
          // we accept the payload as-is — the invalidate below catches
          // any drift on next read.
          queryClient.setQueryData<Contact>(
            ['contact', contactId],
            (prev) =>
              prev
                ? { ...prev, ...contactPatch }
                : (contactPatch as Contact),
          );
          patchConversationCaches((list) =>
            patchConversationContactCache(list, contactId, contactPatch) as ConvForCache[],
          );
          void reconcileContactMetadata(contactId);

          // WhatsApp username → phone handoff can merge this contact with a
          // legacy phone-based contact. The event payload is intentionally
          // additive and older deployments don't always include the merge
          // pointer, so remove any cached list row that now shares the newly
          // received phone and let the server decide the canonical record.
          const updatedPhone =
            typeof contactPatch.phone === 'string' ? contactPatch.phone.trim() : '';
          const mergedIntoId = (contactPatch as Contact & { merged_into_id?: string })
            .merged_into_id;
          if (mergedIntoId && mergedIntoId !== contactId) {
            queryClient.removeQueries({ predicate: query => currentContactMetadata(query.queryKey, contactId) });
            void reconcileContactMetadata(mergedIntoId);
          }
          if (updatedPhone || mergedIntoId) {
            queryClient.setQueriesData<import('@blazeteams/sdk').ContactResponse[]>(
              { predicate: query => currentContactList(query.queryKey) },
              (previous) => {
                if (!Array.isArray(previous)) return previous;
                return previous.filter((contact) => {
                  if (contact.id === contactId) return true;
                  return !updatedPhone || contact.phone !== updatedPhone;
                });
              },
            );
          }
        }
        void reconcileContactLists();
        // Conversation cards embed contact summary — refresh so inbox
        // list / chat header pick up the rename / phone / avatar change.
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
        queryClient.invalidateQueries({ queryKey: ['conversation'] });
        break;
      }

      case 'inbox:contact_deleted': {
        const contactId = data?.id as string | undefined;
        if (contactId) {
          // Drop every cache rooted at this contact (detail, stats,
          // conversations, merged, pipeline-status) in one shot.
          queryClient.removeQueries({ predicate: query => currentContactMetadata(query.queryKey, contactId) });
        }
        void reconcileContactLists();
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
        break;
      }

      // ===== Inbox message reactions (customer chat) =====
      // Backend stores reactions as message_type='reaction' rows with
      // metadata.{message_id, emoji}. The reaction broadcast comes via the
      // standard inbox:new_message event when a peer agent reacts; this
      // explicit reaction event arrives from provider-side incoming
      // reactions (customer reacts to our message). Either way the right
      // move is to refetch the conversation messages so the reaction
      // bubble lands in the cache and the UI groups it under the target.
      case 'inbox:message_reaction': {
        if (!conversationId) break;
        queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });
        break;
      }

      // WhatsApp pushes are owned hints, never localized cache rows.
      case 'inbox:wa_account_event':
      case 'inbox:wa_event_acknowledged': {
        const hint = ownedWAEventHint(data, message.tenant_id);
        if (!hint) break;
        void reconcileWAEvents(queryClient, hint.channelId);
        if (type === 'inbox:wa_account_event' && data?.event_type === 'template') {
          void queryClient.invalidateQueries({ predicate: query => query.queryKey[0] === 'inbox' && query.queryKey[1] === 'channel' && query.queryKey[2] === hint.channelId && query.queryKey[3] === 'templates' && query.queryKey.at(-1) === sessionScope(useAuthStore.getState()) });
        }
        break;
      }

      // ===== AI context bubble — the "fuap" =====
      // Auto-fires when the backend regenerates the summary (after N
      // inbound messages OR an explicit /context/refresh). Patch the
      // cache in place so every observer of this conv re-renders with
      // the new summary without an extra GET roundtrip.
      case 'inbox:context_updated': {
        const next=parseContextUpdated(message);if(!next)break;const key=contextKey(next.conversation_id);
        // Cancel an older read before applying the native tuple. A later owned
        // GET reconciles addon metadata; it does not generate another summary.
        void queryClient.cancelQueries({queryKey:key,exact:true});
        queryClient.setQueryData<ConversationContext>(key,previous=>previous?retainNewerContext(previous,{...previous,...next}):previous);
        void queryClient.invalidateQueries({queryKey:key,exact:true});
        break;
      }
      case 'inbox:ai_suggestion': {
        void receiveAshleySuggestionHint(message); break;
      }
      case 'inbox:agent_suggestion': {
        const next=parseAIProposal(message);if(next)useInboxAgentSuggestionStore.getState().setSuggestion(next);break;
      }

      // ===== Audio transcripts (Whisper) =====
      // Fires when the transcription pipeline finishes (or fails). The
      // payload is per-message, so we patch only the matching row in
      // the messages cache rather than invalidating the whole list.
      case 'inbox:audio_transcript': {
        if (!conversationId) break;
        const messageId = data?.message_id as string | undefined;
        const status = data?.status as string | undefined;
        const transcript = (data?.transcript as string | undefined) ?? '';
        if (!messageId) break;
        if ([message.tenant_id, data?.tenant_id].some(owner => owner !== undefined && owner !== sessionTenantId(useAuthStore.getState()))) break;
        useAiSuggestionStore.getState().observeInteraction(conversationId, messageId, true);
        type Msg = import('@/features/inbox/queries').Message;
        queryClient.setQueryData<Msg[]>(
          ['messages', conversationId],
          (prev) => {
            if (!Array.isArray(prev)) return prev;
            return prev.map((m) =>
              m.id === messageId
                ? {
                    ...m,
                    audio_transcript: transcript,
                    audio_transcript_status:
                      status === 'completed' || status === 'failed'
                        ? status
                        : 'completed',
                    audio_transcript_at: new Date().toISOString(),
                  }
                : m,
            );
          },
        );
        break;
      }

      // ===== BTS payment-proof result (BlazeConnector) =====
      // Async outcome of `POST …/send-to-bts` (the 202 only meant "queued").
      // Payload: { conversation_id, message_id, status: approved|rejected|
      // needs_review, amount: string, bank, payment_applied, customerIdentified }.
      // Store the result (banner in the conversation, linked to the receipt
      // message via message_id) and mark the message sent (disables the menu
      // item even if the 202's onSuccess never ran — e.g. another agent sent
      // it, or the page reloaded mid-flight). Deliberately NO sound/desktop
      // notification: this is in-app feedback, not an inbound message.
      case 'inbox:bts_result': {
        const result = readBtsResultHint(message);
        if (!result?.conversation_id) break;
        const conversationId = result.conversation_id;
        const bts = useBtsStore.getState();
        bts.addResult(result);
        if (result.message_id) bts.markSent(result.message_id);
        // The receipt message row + conversation header may now carry updated
        // state, and an applied payment changes the contact's pending balance.
        queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });
        queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
        void reconcilePanelQueries(key => key[0] === 'conversation-billing' && key[1] === conversationId);
        break;
      }

      // ===== Internal Team Chat =====
      case 'message.reaction':
        queryClient.invalidateQueries({ queryKey: ['team-chat', 'channels'] });
        if (channelId) {
          queryClient.invalidateQueries({ queryKey: messageKey('messages',channelId) });
        }
        break;

      case 'channel.created':
        queryClient.invalidateQueries({ queryKey: currentChatChannelsKey() });
        // Join the new channel's Socket.IO room unconditionally: the server
        // only auto-joins memberships that existed at connect time, so
        // without this the creator/initial members never receive message.*
        // for the new channel until they reconnect. The backend rejects
        // non-members, so the emit is cheap and idempotent for everyone else.
        if (channelId) this.joinChannel(channelId);
        break;

      case 'channel.updated':
        queryClient.invalidateQueries({ queryKey: currentChatChannelsKey() });
        if(channelId) queryClient.invalidateQueries({ queryKey: ['team-chat', 'members', channelId] });
        break;

      // Native directed read hint: the authorized GET supplies last_read_at.
      case 'channel.read':
        if (channelId) {
          queryClient.invalidateQueries({ queryKey: ['team-chat', 'members', channelId] });
        }
        break;

      case 'channel.deleted':
        // If it was a voice channel: end our live session (the room no longer
        // exists) and drop its occupancy key so no zombie "N en la voz" badge
        // survives. Both are cheap no-ops for text channels.
        if (channelId) { this.leaveChannel(channelId); void removeLeftChannel(queryClient, channelId); this.tearDownVoiceForChannel(channelId); }
        break;

      case 'channel.member_added':
        queryClient.invalidateQueries({ queryKey: currentChatChannelsKey() });
        if (channelId) {
          queryClient.invalidateQueries({ queryKey: ['team-chat', 'members', channelId] });
          queryClient.invalidateQueries({ queryKey: messageKey('messages',channelId) });
          // If I'M among the added members (or the payload doesn't say who),
          // join the room now so the channel goes live without a reconnect.
          if (shouldJoinOnMemberAdded(data, useAuthStore.getState().user?.id)) {
            this.joinChannel(channelId);
          }
        }
        break;

      case 'channel.member_removed':
        if (channelId) {
          queryClient.invalidateQueries({ queryKey: ['team-chat', 'members', channelId] });
          // Strictly only when it was ME who got removed: leave the room and
          // drop the (now unauthorized) messages cache for the channel.
          if (isSelfMemberRemoved(data, useAuthStore.getState().user?.id)) {
            this.leaveChannel(channelId);
            void removeLeftChannel(queryClient, channelId);
            // If I was in this channel's VOICE room, I lost access too — tear
            // the session down instead of leaving a call I can no longer see.
            this.tearDownVoiceForChannel(channelId);
          } else {
            void queryClient.invalidateQueries({ queryKey: currentChatChannelsKey() });
            void queryClient.invalidateQueries({ queryKey: ['team-chat', 'members', channelId] });
          }
        }
        break;

      // ===== Team-chat mention directed at ME =====
      // Payload: { message_id, channel_id, user_id, user_name, content }.
      // Same pref gates as maybeNotifyOnNewMessage, plus the dedicated
      // `mentions` toggle (default ON when prefs haven't loaded).
      case 'message.mention': {
        const prefs = queryClient.getQueryData<NotificationSettings>(notificationSettingsKey());
        // TC-22: the per-channel pref gates this too — 'mentions' still
        // notifies (isMention=true), only an explicit 'none' (full mute —
        // local OR server is_muted, Q4) silences it. Missing channel_id
        // defaults to 'all' (notify).
        if (
          prefs?.mentions !== false &&
          this.shouldNotifyTeamChannel(channelId, data, prefs, true)
        ) {
          const notif = buildMentionNotification(data);
          // `message.new` may have already handled this exact message when the
          // channel preference is “Todos”. Enqueue first and let the session
          // event-id ledger suppress a second alert/sound for `message.mention`.
          const isNewAttentionEvent = channelId
            ? this.enqueueTeamChatLiveAlert(
                data,
                channelId,
                notif.senderName,
                true,
              )
            : true;
          if (isNewAttentionEvent && prefs?.sound !== false) notificationSound.play('mention');
          if (isNewAttentionEvent && prefs?.desktop !== false) {
            const copy = messageAlertCopy(data);
            showNotification(notif.title, {
              body: copy.previewCopy
                ? alertPreview(previewProductCopy(copy.previewCopy), 120)
                : notif.body,
              // Group/close by channel, matching the in-app card. The message
              // id still deduplicates the realtime echo in the alert store.
              tag: channelId ?? notif.tag,
              requireInteraction: !!channelId,
              onClick: channelId
                ? () => {
                    useTeamChatLiveAlertStore.getState().acknowledge(channelId);
                    useChatDockStore.getState().openChat(channelId);
                  }
                : undefined,
            });
          }
        }
        // Unread/mention badges live on the channel list.
        queryClient.invalidateQueries({ queryKey: ['team-chat', 'channels'] });
        break;
      }

      // ===== Hilos con seguimiento (thread tracking) =====
      // // contrato propuesto #93497 — el backend confirmará shapes; los
      // handlers toleran envelope {type,data} o payload plano (la extracción
      // de channel_id/parent_message_id arriba ya cubre ambos) y campos
      // ausentes. Regla del repo: la invalidación vive AQUÍ, no en listeners
      // de componentes.
      // ===== Team-chat channel typing =====
      // Server re-emits our `typing` { channel_id } enriched with
      // user_id/user_name to the channel room (excluding the emitter). Write
      // into the shared typing store under the 'channel:'-prefixed key —
      // same 4s TTL + 1s sweep as the inbox lane.
      case 'typing': {
        const evt = parseChannelTypingEvent(data, message, useAuthStore.getState().user?.id);
        if (evt) {
          useTypingStore
            .getState()
            .setTyping(channelTypingKey(evt.channelId), {
              userId: evt.userId,
              userName: evt.userName,
            });
        }
        break;
      }

      // Recording hints load only when delivered; owner is fenced across the
      // async chunk load and the consumer performs owned GETs, never media.
      case 'meeting:recording_changed': {
        const revision = sessionRevision();
        void import('@/features/meetings/recording-hints').then(({ receiveRecordingHint }) => {
          if (revision === sessionRevision()) receiveRecordingHint(queryClient, message);
        }).catch(() => {});
        break;
      }

      // ===== Meetings =====
      case 'meeting:started':
      case 'meeting:ended':
      case 'meeting:joined':
      case 'meeting:left':
      case 'meeting:invited':
        queryClient.invalidateQueries({ queryKey: ['meetings'] });
        if (meetingId) {
          queryClient.invalidateQueries({ queryKey: ['meeting', meetingId] });
        }
        break;

      // Native private call hints are scoped to this recipient/workspace.
      case 'call:ringing':
      case 'call:accepted':
      case 'call:participant_joined':
      case 'call:participant_left':
      case 'call:rejected':
      case 'call:ended':
      case 'call:timeout':
        receiveCallHint(queryClient, message);
        break;

      // ===== Kanban =====
      case 'kanban:pipeline_changed':
        queryClient.invalidateQueries({ predicate: query => isCurrentKanbanKey(query.queryKey, 'pipelines') });
        invalidateContactPipelineStatuses();
        if (pipelineId) {
          queryClient.invalidateQueries({ predicate: query => isCurrentKanbanKey(query.queryKey, 'stages', pipelineId) });
          queryClient.invalidateQueries({ predicate: query => isCurrentKanbanKey(query.queryKey, 'cards', pipelineId) });
        }
        break;
      case 'kanban:stage_changed':
        queryClient.invalidateQueries({ predicate: query => isCurrentKanbanKey(query.queryKey, 'pipelines') });
        invalidateContactPipelineStatuses();
        if (pipelineId) {
          queryClient.invalidateQueries({ predicate: query => isCurrentKanbanKey(query.queryKey, 'stages', pipelineId) });
          queryClient.invalidateQueries({ predicate: query => isCurrentKanbanKey(query.queryKey, 'cards', pipelineId) });
        }
        break;
      case 'kanban:card_moved':
      case 'kanban:card_created':
      case 'kanban:card_updated':
      case 'kanban:card_deleted':
        if (pipelineId) {
          queryClient.invalidateQueries({ predicate: query => isCurrentKanbanKey(query.queryKey, 'cards', pipelineId) });
        } else {
          queryClient.invalidateQueries({ predicate: query => isCurrentKanbanKey(query.queryKey, 'cards') });
        }
        queryClient.invalidateQueries({ predicate: query => isCurrentKanbanKey(query.queryKey, 'pipelines') });
        queryClient.invalidateQueries({ predicate: query => isCurrentKanbanKey(query.queryKey, 'card') });
        invalidateContactPipelineStatuses();
        if (typeof data?.contact_id === 'string') {
          queryClient.invalidateQueries({
            queryKey: ['contact', data.contact_id, 'pipeline-status'],
          });
        }
        break;

      // ===== Chatwoot migration =====
      // Backend emits these per-user during the migration job. They're
      // ephemeral progress signals (not query-cache state) so we surface them
      // via a window CustomEvent the wizard can subscribe to. This keeps the
      // realtime manager from importing migration-specific state.
      case 'migration:progress':
      case 'migration:entity_complete':
      case 'migration:complete':
      case 'migration:failed':
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('migration-event', { detail: { type, data: data ?? {} } }),
          );
        }
        break;

      // ===== Presence =====
      // Bulk presence snapshot — server pushes the current online roster on
      // socket connect so we hydrate everyone in one go instead of waiting
      // for each user to flip individually. Backend payload shape varies:
      // it may be `{ users: [{ id, status, last_seen }, ...] }` or a flat
      // array of user IDs (only online users). Handle both defensively.
      case 'online_users': {
        const data = (message as { data?: unknown }).data ?? message;
        const raw = (data as { users?: unknown; user_ids?: unknown; ids?: unknown });
        const list =
          (Array.isArray(raw.users) ? raw.users : null) ??
          (Array.isArray(raw.user_ids) ? raw.user_ids : null) ??
          (Array.isArray(raw.ids) ? raw.ids : null) ??
          (Array.isArray(data) ? data : null);
        if (!list) break;
        const entries = list.map((entry: unknown) => {
          if (typeof entry === 'string') {
            return { id: entry, presence: 'online' as const };
          }
          const e = entry as { id?: string; user_id?: string; status?: string; presence?: string; last_seen?: string };
          return {
            id: e.id ?? e.user_id ?? '',
            presence: e.presence ?? e.status ?? 'online',
            lastSeen: e.last_seen,
          };
        }).filter((e) => e.id);
        usePresenceStore.getState().hydrate(entries);
        break;
      }

      case 'user.presence_changed': {
        // Surgical update of the presence store — no query invalidations.
        // The directory and agent queries hold a `presence`/`status` field
        // that we no longer rely on for live updates; consumers read via
        // `usePresence(userId)` from the store instead.
        const userId = (data?.user_id ?? message.user_id) as string | undefined;
        const rawStatus = (data?.presence ?? data?.status ?? message.presence) as
          | string
          | undefined;
        const lastSeen = (data?.last_seen ?? message.last_seen) as string | undefined;
        if (userId) {
          usePresenceStore.getState().setPresence(userId, normalizeStatus(rawStatus), lastSeen);
        }
        break;
      }

      // ===== Voice channels: cross-room occupancy ("N en la voz de #canal") =====
      // In-room state (roster/speaking/mute) comes from the LiveKit SDK inside
      // VoiceRoom. Private hints only request an owned occupancy HTTP reread.
      case 'voice:channel_occupancy':
      case 'voice:participant_joined':
      case 'voice:participant_left':
        receiveVoiceHint(queryClient,message);
        break;

      default:
        if (import.meta.env.DEV) {
          console.log('[Realtime] Unhandled event:', type, message);
        }
    }
  }

  private async notifyOwnedTrackingHint(hint:TrackingHint,owner:ReturnType<typeof captureChatOwner>){
    try{
      const selector={...messageReadSelector('thread',hint.parentId),from_end:true};
      const key=[...messageKey('thread',hint.parentId,owner),'tracking-notification-read',hint.type,hint.eventId];
      const result=await queryClient.fetchQuery({queryKey:key,queryFn:({signal})=>readOwnedMessages(owner,selector,signal),staleTime:0,retry:false,gcTime:0});
      if(!isCurrentMessageOwner(owner)||hint.type==='thread:tracking_updated')return;
      const parent=result.parent,tracking=parent?.tracking;
      if(!parent||parent.channel_id!==hint.channelId||!tracking)return;
      let byName:string|undefined;
      if(hint.type==='thread:assigned'){
        if(tracking.assignee_id!==owner.user)return;
        if(hint.eventId===parent.id){if(parent.user_id===owner.user)return;byName=parent.user_name;}
        else {const audit=result.messages.find(row=>row.id===hint.eventId);if(!audit?.system_copy||audit.user_id===owner.user||!['tracking.created','tracking.updated'].includes(audit.system_copy.kind)||!audit.system_copy.changes.some(change=>change.field==='assignee'))return;byName=audit.system_copy.actor_name;}
      }else if(tracking.status==='resolved'||chatInstant(tracking.due_at??undefined)!==chatInstant(hint.dueAt)||tracking.assignee_id&&tracking.assignee_id!==owner.user)return;
      const prefs=queryClient.getQueryData<NotificationSettings>(notificationSettingsKey());
      if(!this.shouldNotifyTeamChannel(hint.channelId,{},prefs,false))return;
      const previewCopy={kind:hint.type==='thread:assigned'?'thread_assigned' as const:'thread_due' as const,title:tracking.title,byName,dueAt:hint.dueAt};
      const channel=currentChatChannel(queryClient,hint.channelId);
      const fresh=useTeamChatLiveAlertStore.getState().enqueue({eventId:hint.type+':'+hint.eventId,channelId:hint.channelId,channelName:channel?.name??'',isDirect:channel?.type==='direct',senderName:byName??'',preview:'',previewCopy,createdAt:parent.updated_at});
      if(!fresh||!isCurrentMessageOwner(owner))return;
      if(prefs?.sound!==false)notificationSound.play('agent_message');
      if(prefs?.desktop!==false){const copy=buildThreadNotification(hint.type,{title:tracking.title,assigned_by_name:byName,due_at:hint.dueAt});showNotification(copy.title,{body:copy.body,tag:hint.channelId,onClick:()=>{if(isCurrentMessageOwner(owner)){useTeamChatLiveAlertStore.getState().acknowledge(hint.channelId);useChatDockStore.getState().openChat(hint.channelId);}}});}
    }catch{/* No owned GET means no verified notification. */}
  }

  private async notifyOwnedChatHint(hint:NonNullable<ReturnType<typeof ownedMessageHint>>,owner:ReturnType<typeof captureChatOwner>) {
    try{
      const selector={...messageReadSelector(hint.parentMessageId?'thread':'list',hint.parentMessageId??hint.channelId),...(hint.parentMessageId?{from_end:true}:{})};
      // Both transports share the same message hint read. Different message
      // IDs must not reuse a snapshot started before the newer event arrived.
      const key=messageKey(hint.parentMessageId?'thread':'messages',hint.parentMessageId??hint.channelId,owner);
      const result=await queryClient.fetchQuery({queryKey:[...key,'notification-read',hint.messageId],queryFn:({signal})=>readOwnedMessages(owner,selector,signal),staleTime:0,retry:false,gcTime:0});
      if(!isCurrentMessageOwner(owner))return;
      const verified=result.messages.find(message=>message.id===hint.messageId);
      const row=verified?{...verified}:undefined;
      if(!row||row.channel_id!==hint.channelId||row.user_id===owner.user||row.system_copy)return;
      const isMention=row.mentions?.includes(owner.user!)??false;
      if(hint.type==='message.mention'&&!isMention)return;
      const prefs=queryClient.getQueryData<NotificationSettings>(notificationSettingsKey());
      if(!this.shouldNotifyTeamChannel(hint.channelId,row,prefs,isMention)||isMention&&prefs?.mentions===false)return;
      const fresh=this.enqueueTeamChatLiveAlert(row,hint.channelId,row.user_name??commsText('teamMessage'),isMention);
      if(!fresh||!isCurrentMessageOwner(owner))return;
      if(prefs?.sound!==false)notificationSound.play(isMention?'mention':'agent_message');
      if(prefs?.desktop!==false){const copy=messageAlertCopy(row);showNotification(isMention?buildMentionNotification(row).title:row.user_name??commsText('teamMessage'),{
        body:copy.previewCopy?alertPreview(previewProductCopy(copy.previewCopy),120):alertPreview(row.content,120),tag:hint.channelId,
        onClick:()=>{if(isCurrentMessageOwner(owner)){useTeamChatLiveAlertStore.getState().acknowledge(hint.channelId);useChatDockStore.getState().openChat(hint.channelId);}}});}
    }catch{/* Failed owned GET supplies neither history nor a notification. */}
  }

  /**
   * Tear down any voice state tied to a channel the user just lost
   * (`channel.deleted`, or a self `channel.member_removed`):
   *   - if OUR live voice session is in that channel, leave it — the session
   *     store clears, `VoiceDock` unmounts `VoiceRoom`, and the LiveKit room
   *     disconnects cleanly;
   *   - prune the channel's occupancy key so the "N en la voz" badge state
   *     can't go zombie for a room that no longer exists.
   */
  private tearDownVoiceForChannel(channelId: string) {
    const session = useVoiceSessionStore.getState();
    if (shouldTearDownVoice(session.channelId, channelId)) {
      session.leave();
    }
    useVoicePresenceStore.getState().prune(channelId);
  }

  /**
   * Q4: the EFFECTIVE per-channel notify pref — the local 3-state pref merged
   * with the server's mute. The server flag isn't on the event payload, so we
   * read the channel row from the `['team-chat','channels']` cache (the same
   * cache every channel.* event keeps fresh); my member row's `is_muted`
   * resolves the server state (see `serverChannelMuted`). Cache miss (channel
   * not loaded yet) degrades to the local pref alone.
   */
  private channelNotifyPref(channelId: string): ChannelNotifyPref {
    const localPref = getChannelNotifyPref(channelId);
    if (!channelId) return localPref;
    const channel = currentChatChannel(queryClient,channelId);
    return effectiveNotifyPref(channel, localPref, useAuthStore.getState().user?.id);
  }

  /** Cached channel row used to resolve DM-vs-channel preferences and labels. */
  private teamChatChannel(channelId: string | undefined): Channel | undefined {
    if (!channelId) return undefined;
    return currentChatChannel(queryClient,channelId);
  }

  /** Avoid repeated alerts for exact chats already open in the focused app. */
  private isTeamChatChannelActivelyVisible(channelId: string): boolean {
    return isTeamChatAttended(
      channelId,
      useChatDockStore.getState(),
      useTeamChatLiveAlertStore.getState().activeFullViewChannelId,
      isDocumentAttended(),
    );
  }

  /** Category + per-channel mute + active-view gate shared by new messages and mentions. */
  private shouldNotifyTeamChannel(
    channelId: string | undefined,
    data: WSMessage['data'],
    prefs: NotificationSettings | undefined,
    isMention: boolean,
  ): boolean {
    if (!channelId) return false;
    const channel = this.teamChatChannel(channelId);
    const payloadType = typeof data?.channel_type === 'string' ? data.channel_type : undefined;
    const channelType = channel?.type ?? payloadType;
    const categoryEnabled =
      channelType === 'direct'
        ? prefs?.directMessages !== false
        : channelType
          ? prefs?.channels !== false
          : // A cold cache cannot tell a DM from a channel. Notify if either
            // category remains enabled instead of silently losing the event.
            prefs?.directMessages !== false || prefs?.channels !== false;

    return (
      categoryEnabled &&
      shouldNotifyChannelMessage(this.channelNotifyPref(channelId), isMention) &&
      !this.isTeamChatChannelActivelyVisible(channelId)
    );
  }

  /** Add/update the non-expiring in-app alert and return false for a duplicate event. */
  private enqueueTeamChatLiveAlert(
    data: WSMessage['data'],
    channelId: string,
    senderName: string,
    isMention = false,
  ): boolean {
    const channel = this.teamChatChannel(channelId);
    const payloadType = typeof data?.channel_type === 'string' ? data.channel_type : undefined;
    const isDirect = (channel?.type ?? payloadType) === 'direct';
    const payloadChannelName =
      typeof data?.channel_name === 'string' ? data.channel_name : '';
    const channelName =
      isDirect && channel
        ? dmDisplayName(channel, useAuthStore.getState().user?.id ?? null,undefined,'')
        : channel?.name || payloadChannelName || '';
    const rawEventId = data?.id ?? data?.message_id;
    const createdAt =
      typeof data?.created_at === 'string' ? data.created_at : new Date().toISOString();
    const content = typeof data?.content === 'string' ? data.content : '';
    const fileName = typeof data?.file_name === 'string' ? data.file_name : '';
    const {preview,previewCopy}=messageAlertCopy(data);
    const eventId =
      typeof rawEventId === 'string' && rawEventId
        ? rawEventId
        : `${channelId}:${String(data?.user_id ?? '')}:${createdAt}:${content}:${previewCopy?.kind??''}:${fileName}`;

    return useTeamChatLiveAlertStore.getState().enqueue({
      eventId,
      channelId,
      channelName,
      isDirect,
      senderName:typeof data?.sender_name==='string'&&data.sender_name?senderName:typeof data?.user_name==='string'&&data.user_name?senderName:'',
      preview,
      ...(previewCopy?{previewCopy}:{}),
      createdAt,
      isMention,
    });
  }

  /**
   * Sound + desktop + persistent in-app notification gate for inbound
   * messages. Reads user prefs from the TanStack cache
   * (`['notifications','settings',sessionScope]`), so toggling them in Settings →
   * Notifications takes effect on the next event. Skips own messages so the
   * agent doesn't get pinged by their own outgoing replies.
   */
  private maybeNotifyOnNewMessage(
    data: WSMessage['data'],
    conversationId: string | undefined,
    channelId: string | undefined,
  ) {
    if (!data) return;

    // Inbox broadcasts are tenant-wide. Do not surface sound/desktop UI for a
    // conversation the server-scoped list does not contain (strict assignment
    // and optional inbox_channel_scoping both depend on this).
    if (conversationId && !isConversationInVisibleLists(conversationId)) return;

    const prefs = queryClient.getQueryData<NotificationSettings>(notificationSettingsKey());
    const selfId = useAuthStore.getState().user?.id;
    const senderUserId = (data.user_id ?? data.sender_user_id) as string | undefined;
    const direction = data.direction as string | undefined;

    // Customer inbox: notify only on inbound (the customer wrote, not us).
    // Team chat: notify on any message that isn't from the current user.
    const isCustomerInbound = !!conversationId && direction === 'inbound';
    const isTeamForeign = !!channelId && !!senderUserId && senderUserId !== selfId;
    if (!isCustomerInbound && !isTeamForeign) return;

    const soundOn = prefs?.sound !== false;
    const desktopOn = prefs?.desktop !== false;

    // Inbox retains the existing transient sound/desktop behavior. Persistent
    // acknowledgement cards are intentionally scoped to internal team chat.
    if (isCustomerInbound) {
      if (!soundOn && !desktopOn) return;
      const senderName =typeof data.sender_name==='string'&&data.sender_name?data.sender_name:typeof data.user_name==='string'&&data.user_name?data.user_name:commsText('newMessage');
      const body = alertPreview(typeof data.content==='string'?data.content:'',120);
      if (soundOn) notificationSound.play('customer_message');
      if (desktopOn) {
        showNotification(senderName, { body, tag: conversationId });
      }
      return;
    }

    // TC-22: per-channel preference (todos / solo menciones / nada), merged
    // with the server-side mute (Q4 — see channelNotifyPref). A plain
    // channel message is never a mention here — mentions arrive via the
    // dedicated `message.mention` event, which gates itself with
    // isMention=true. Customer-inbox notifications are unaffected.
    if (!this.shouldNotifyTeamChannel(channelId, data, prefs, false)) {
      return;
    }

    const senderName =typeof data.sender_name==='string'&&data.sender_name?data.sender_name:typeof data.user_name==='string'&&data.user_name?data.user_name:commsText('teamMessage');
    const messageCopy=messageAlertCopy(data);
    const body = alertPreview(messageCopy.previewCopy?previewProductCopy(messageCopy.previewCopy):messageCopy.preview,120);
    const teamChannelId = channelId;
    if (!teamChannelId) return;
    const isNewAttentionEvent = this.enqueueTeamChatLiveAlert(data, teamChannelId, senderName);
    if (!isNewAttentionEvent) return;

    if (soundOn) {
      notificationSound.play('agent_message');
    }
    if (desktopOn) {
      showNotification(senderName, {
        body,
        // Tag dedupes notifications from the same conversation/channel so
        // multiple incoming messages stack instead of piling N popups.
        tag: teamChannelId,
        requireInteraction: true,
        onClick: () => {
          useTeamChatLiveAlertStore.getState().acknowledge(teamChannelId);
          useChatDockStore.getState().openChat(teamChannelId);
        },
      });
    }
  }

  /**
   * Notify peers that the current user is composing a reply in a
   * conversation. Throttled to once per 2.5s per conversation — calling
   * this on every keystroke is the expected pattern.
   *
   * Backend (`internal/socketio/events.go:263`) listens for the
   * `inbox:typing` event with payload `{ conversation_id }` and re-emits
   * it to other tenant clients enriched with `user_id` + `user_name`.
   */
  sendTyping(conversationId: string) {
    if (!conversationId || !this.socket?.connected) return;
    const now = Date.now();
    const last = this.lastTypingEmit.get(conversationId) ?? 0;
    if (now - last < 2500) return;
    this.lastTypingEmit.set(conversationId, now);
    this.socket.emit('inbox:typing', { conversation_id: conversationId });
  }

  /**
   * Team-chat sibling of `sendTyping`: notify channel members that the
   * current user is composing. The backend re-emits `typing` to the
   * channel's room enriched with user_id/user_name, excluding the emitter.
   * Throttled to once per 2.5s per channel — call it on every keystroke.
   */
  sendChannelTyping(channelId: string) {
    if (!channelId || !this.socket?.connected) return;
    const now = Date.now();
    const last = this.lastChannelTypingEmit.get(channelId) ?? 0;
    if (now - last < 2500) return;
    this.lastChannelTypingEmit.set(channelId, now);
    this.socket.emit('typing', { channel_id: channelId });
  }

  disconnect() {
    document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.metricsTimer) clearTimeout(this.metricsTimer);
    this.metricsTimer = null;
    this.reconnectAttempt = 0;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.connectionToken = null;
    this.connectionRevision = null;
    this.socket?.removeAllListeners();
    this.socket?.offAny();
    this.socket?.disconnect();
    if (this.ws) {
      // Closing an old session must not schedule a reconnect or dispatch queued events.
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.close();
    }
    this.socket = null;
    this.ws = null;
    this.lastTypingEmit.clear();
    this.lastChannelTypingEmit.clear();
    this.joinedChannels.clear();
    this.pendingJoins.clear();
    this.viewingConversation = null;
    // Logout: viewer presence must not leak into the next session.
    useInboxPresenceStore.getState().clear();
  }
}

export const realtimeManager = new RealtimeManager();
